import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { SEOHead } from "@/components/SEOHead";
import { Ticket, Calendar, MapPin, Send, Clock, Archive, Repeat, XCircle, CalendarPlus, Mail, Eye, MoreHorizontal, Award, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { OrderStatusBadge } from "@/components/OrderStatusBadge";
import { EventImage } from "@/components/EventImage";
import { TransferTicketModal } from "@/components/TransferTicketModal";
import { ResaleListingModal } from "@/components/ResaleListingModal";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { getMyTickets } from "@/lib/api";
import { cancelResaleListing, getMyResaleListings } from "@/lib/api-resale";
import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { normalizeText } from "@/lib/search";
import { useToast } from "@/hooks/use-toast";
import { generateGoogleCalendarUrl } from "@/lib/calendar";
import { resolveTicketQrCode } from "@/lib/ticket-qr";
import { storageKeys } from "@/lib/storage";
import { generateCertificatePDF } from "@/lib/certificate-pdf-client";
import {
  baixarCertificadoDoServidor,
  DownloadRecusado,
} from "@/lib/certificate-download";
import { formatCertificateEventDate } from "@/lib/certificate-date";
import { invokeAuthenticatedFunction } from "@/lib/api-certificates";
import {
  CertificatePreview,
  type CertificateFields,
  type CertificateFontSizes,
  type CertificateSampleData,
  type CertificateSigner,
  type CertificateTextConfig,
  type CertificateTextPositions,
} from "@/components/certificates/CertificatePreview";
import type { CertificateElement } from "@/components/certificates/CertificateElementTypes";

type TabId = "active" | "pending" | "cancelled" | "past" | "archived";

const DEFAULT_CERT_FIELDS: CertificateFields = {
  showEventName: true,
  showParticipantName: true,
  showParticipantLastName: true,
  showCPF: false,
  maskCPF: false,
  showEventDate: true,
  eventDateMode: "start",
  showEventTime: false,
  showEventLocation: true,
  showWorkload: false,
  showSigners: true,
  showQRCode: true,
};

const DEFAULT_CERT_TEXT: CertificateTextConfig = {
  title: "CERTIFICADO DE PARTICIPAÇÃO",
  introText: "Certificamos que",
  participationText: "participou do evento",
  conclusionText: "Comprove sua participação através do código de verificação.",
};

const DEFAULT_CERT_POSITIONS: CertificateTextPositions = {
  titleTop: 80,
  nameTop: 55,
  metadataTop: 35,
};

const DEFAULT_CERT_FONT_SIZES: CertificateFontSizes = {
  title: "xl",
  name: "4xl",
  event: "2xl",
};

interface CertificateDownloadPayload {
  fileName: string;
  sampleData: CertificateSampleData;
  config: {
    backgroundUrl?: string;
    fields: CertificateFields;
    textConfig: CertificateTextConfig;
    signers: CertificateSigner[];
    workloadHours?: number;
    textColor: string;
    textPositions: CertificateTextPositions;
    fontSizes: CertificateFontSizes;
    elements?: CertificateElement[];
  };
}

export default function MeusIngressos() {
  const { t } = useTranslation("buyer");
  const { user, session } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>("active");
  const [searchQuery, setSearchQuery] = useState("");

  const [transferModal, setTransferModal] = useState<{
    open: boolean;
    ticketId: string;
    eventTitle: string;
    tierName: string;
  }>({ open: false, ticketId: "", eventTitle: "", tierName: "" });

  const [resaleModal, setResaleModal] = useState<{
    open: boolean;
    ticket: any;
  }>({ open: false, ticket: null });
  const [isEmittingCertificateFor, setIsEmittingCertificateFor] = useState<string | null>(null);
  const [downloadPayload, setDownloadPayload] = useState<CertificateDownloadPayload | null>(null);
  const hiddenCertificateRef = useRef<HTMLDivElement>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: myResaleListings } = useQuery({
    queryKey: ["my-resale-listings", user?.id],
    queryFn: () => getMyResaleListings(user!.id),
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  // Realtime: auto-update when orders/tickets change (e.g. boleto paid)
  useRealtimeSubscription({
    table: "orders",
    filter: user?.id ? `buyer_id=eq.${user.id}` : undefined,
    queryKey: ["my-tickets", user?.id],
    enabled: !!user?.id,
  });

  const { data: tickets, isLoading } = useQuery({
    queryKey: ["my-tickets", user?.id],
    queryFn: () => getMyTickets(user!.id),
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (!downloadPayload) return;

    const run = async () => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 300));
        const previewEl = hiddenCertificateRef.current?.querySelector<HTMLElement>("[data-testid='certificate-preview']");
        if (!previewEl) {
          throw new Error("Elemento de preview não encontrado.");
        }
        await generateCertificatePDF(previewEl, downloadPayload.fileName);
        toast({
          title: t("tickets.toasts.certEmittedTitle"),
          description: t("tickets.toasts.certEmittedDesc"),
        });
      } catch (err: any) {
        toast({
          title: t("tickets.toasts.certErrorTitle"),
          description: err?.message || t("tickets.toasts.certErrorDesc"),
          variant: "destructive",
        });
      } finally {
        setDownloadPayload(null);
        setIsEmittingCertificateFor(null);
      }
    };

    const timer = setTimeout(run, 100);
    return () => clearTimeout(timer);
  }, [downloadPayload, toast, t]);

  const handleEmitCertificate = async (ticket: any) => {
    if (!ticket?.id) return;

    setIsEmittingCertificateFor(ticket.id);
    try {
      const generationData = await invokeAuthenticatedFunction<any>("generate-certificate", {
        ticketId: ticket.id,
      }, session?.access_token ?? undefined);

      const certificateId = generationData?.certificate?.id as string | undefined;
      if (!certificateId) {
        throw new Error(t("tickets.toasts.certMissingId"));
      }

      /*
       * O servidor primeiro.
       *
       * A edge `render-certificate` desenha do snapshot congelado na emissão —
       * o mesmo documento que vai no e-mail. Só depois dela é que vale a pena
       * buscar o config e montar preview: o caminho abaixo existe como recuo
       * para certificado emitido antes da Fase B, ou edge não publicada.
       */
      try {
        if (
          await baixarCertificadoDoServidor(
            { certificateId },
            `certificado-${certificateId}.pdf`,
          )
        ) {
          toast({
            title: t("tickets.toasts.certEmittedTitle"),
            description: t("tickets.toasts.certEmittedDesc"),
          });
          // Sem isto o botão fica girando para sempre: quem limpa este estado
          // no caminho antigo é o efeito do preview escondido, que aqui nem
          // chega a montar.
          setIsEmittingCertificateFor(null);
          return;
        }
      } catch (err) {
        if (err instanceof DownloadRecusado) {
          toast({
            title: t("tickets.toasts.certErrorTitle"),
            description: err.message,
            variant: "destructive",
          });
          setIsEmittingCertificateFor(null);
          return;
        }
        throw err;
      }

      const certData = await invokeAuthenticatedFunction<any>("generate-certificate-pdf", {
        certificateId,
      }, session?.access_token ?? undefined);

      if (!certData || certData.mode !== "certificate") {
        throw new Error(t("tickets.toasts.certInvalidData"));
      }

      const certificateConfig = certData.certificateConfig || {};
      const certificate = certData.certificate || {};
      const certificateEvent = certData.event || {};

      const fields = { ...DEFAULT_CERT_FIELDS, ...(certificateConfig.fields || {}) };
      const textConfig = { ...DEFAULT_CERT_TEXT, ...(certificateConfig.textConfig || {}) };
      const signers = Array.isArray(certificateConfig.signers) ? certificateConfig.signers : [];
      const textPositions = { ...DEFAULT_CERT_POSITIONS, ...(certificateConfig.textPositions || {}) };
      const fontSizes = { ...DEFAULT_CERT_FONT_SIZES, ...(certificateConfig.fontSizes || {}) };

      const eventDate = formatCertificateEventDate(
        certificateEvent.start_date,
        certificateEvent.end_date,
        fields.eventDateMode,
        (d) => d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }),
      );

      const eventTime = certificateEvent.start_date
        ? new Date(certificateEvent.start_date).toLocaleTimeString("pt-BR", {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "";

      setDownloadPayload({
        fileName: `certificado-${certificate.certificate_code || ticket.id}.pdf`,
        sampleData: {
          eventName: certificateEvent.title || ticket.events?.title || "",
          participantName: certificate.attendee_name || ticket.attendee_name || "",
          participantCPF: ticket.attendee_cpf || "",
          eventDate,
          eventTime,
          eventLocation: certificateEvent.venue_name || ticket.events?.venue_name || "",
          certificateCode: certificate.certificate_code || "",
        },
        config: {
          backgroundUrl: certificateConfig.backgroundUrl || undefined,
          fields,
          textConfig,
          signers,
          workloadHours: certificate.workload_hours ?? certificateConfig.workloadHours ?? 0,
          textColor: certificateConfig.textColor || "#1f2937",
          textPositions,
          fontSizes,
          elements: certificateConfig.elements,
        },
      });
    } catch (err: any) {
      setIsEmittingCertificateFor(null);
      toast({
        title: t("tickets.toasts.certEmitFailTitle"),
        description: err?.message || t("tickets.toasts.certEmitFailDesc"),
        variant: "destructive",
      });
    }
  };

  const now = useMemo(() => new Date(), []);
  const [archivedIds, setArchivedIds] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKeys.archivedTickets(user?.id ?? null)) || "[]");
    } catch { return []; }
  });

  // Reload archived ids when user changes
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(storageKeys.archivedTickets(user?.id ?? null)) || "[]");
      setArchivedIds(Array.isArray(stored) ? stored : []);
    } catch {
      setArchivedIds([]);
    }
  }, [user?.id]);

  const toggleArchive = (ticketId: string) => {
    setArchivedIds((prev) => {
      const next = prev.includes(ticketId) ? prev.filter((id) => id !== ticketId) : [...prev, ticketId];
      localStorage.setItem(storageKeys.archivedTickets(user?.id ?? null), JSON.stringify(next));
      return next;
    });
  };

  // Categorize tickets
  const categorizedTickets = useMemo(() => {
    if (!tickets) return { active: [], pending: [], cancelled: [], past: [], archived: [] };

    const active: any[] = [];
    const pending: any[] = [];
    const cancelled: any[] = [];
    const past: any[] = [];
    const archived: any[] = [];

    for (const ticket of tickets) {
      if (archivedIds.includes(ticket.id)) {
        archived.push(ticket);
        continue;
      }

      const eventDate = ticket.events?.start_date ? new Date(ticket.events.start_date) : null;
      const eventEndDate = ticket.events?.end_date
        ? new Date(ticket.events.end_date)
        : ticket.events?.start_date
          ? new Date(ticket.events.start_date)
          : null;
      const isPastEvent = eventEndDate && eventEndDate < now;

      if (ticket.status === "cancelled" || ticket.status === "refunded") {
        cancelled.push(ticket);
      } else if (ticket.status === "pending" || ticket.status === "reserved") {
        pending.push(ticket);
      } else if (isPastEvent || ticket.status === "used") {
        past.push(ticket);
      } else if (ticket.status === "active") {
        active.push(ticket);
      } else {
        active.push(ticket);
      }
    }

    return { active, pending, cancelled, past, archived };
  }, [tickets, now, archivedIds]);

  // Filter by search query
  const filteredTickets = useMemo(() => {
    const ticketsForTab = categorizedTickets[activeTab];
    if (!searchQuery.trim()) return ticketsForTab;

    const normalizedQuery = normalizeText(searchQuery);
    
    return ticketsForTab.filter((ticket: any) => {
      const searchableFields = [
        ticket.events?.title,
        ticket.events?.venue_city,
        ticket.ticket_tiers?.name,
        ticket.attendee_name,
        ticket.attendee_email,
        ticket.qr_code,
        ticket.id,
        ticket.order_id,
      ].filter(Boolean);

      return searchableFields.some((field) =>
        normalizeText(String(field)).includes(normalizedQuery)
      );
    });
  }, [categorizedTickets, activeTab, searchQuery]);

  const tabs: { id: TabId; label: string; count: number }[] = [
    { id: "active", label: t("tickets.tabs.active"), count: categorizedTickets.active.length },
    { id: "pending", label: t("tickets.tabs.pending"), count: categorizedTickets.pending.length },
    { id: "cancelled", label: t("tickets.tabs.cancelled"), count: categorizedTickets.cancelled.length },
    { id: "past", label: t("tickets.tabs.past"), count: categorizedTickets.past.length },
    { id: "archived", label: t("tickets.tabs.archived"), count: categorizedTickets.archived.length },
  ];

  const isPast = activeTab === "past" || activeTab === "cancelled" || activeTab === "archived";

  const handleCancelResale = async (listingId: string, ticketId: string) => {
    try {
      await cancelResaleListing(listingId, ticketId);
      toast({ title: t("tickets.toasts.resaleCancelledTitle"), description: t("tickets.toasts.resaleCancelledDesc") });
      queryClient.invalidateQueries({ queryKey: ["my-tickets"] });
      queryClient.invalidateQueries({ queryKey: ["my-resale-listings"] });
      queryClient.invalidateQueries({ queryKey: ["resale-listings"] });
    } catch (err: any) {
      toast({ title: t("tickets.toasts.emailErrorTitle", "Erro"), description: err.message, variant: "destructive" });
    }
  };

  const isTicketEligibleForCertificate = (ticket: any): boolean => {
    const event = ticket.events;
    if (!event?.has_certificates) return false;
    const eligibleTierIds = event.certificate_config?.eligible_tier_ids;
    if (!Array.isArray(eligibleTierIds) || eligibleTierIds.length === 0) return true;
    return eligibleTierIds.includes(ticket.tier_id);
  };

  const renderTicket = (ticket: any) => {
    const isTransferable = ticket.status === "active" && ticket.ticket_tiers?.is_transferable !== false;
    const isResellable =
      ticket.status === "active" &&
      ticket.ticket_tiers?.is_resellable !== false &&
      ticket.events?.allow_resale === true;
    const isForResale = ticket.is_for_resale === true;
    const activeListing = (myResaleListings || []).find((l: any) => l.ticket_id === ticket.id && l.status === "active");
    const eventDate = ticket.events?.start_date ? new Date(ticket.events.start_date) : null;
    const eventEndDate = ticket.events?.end_date
      ? new Date(ticket.events.end_date)
      : ticket.events?.start_date
        ? new Date(ticket.events.start_date)
        : null;
    const isToday = eventDate && eventDate.toDateString() === now.toDateString();
    const eligibleForCertificate = isTicketEligibleForCertificate(ticket);
    const eventHasPassed = eventEndDate && eventEndDate < now;
    const isCertificateOptedOut = ticket.certificate_opt_out === true;
    const canEmitCertificate = eligibleForCertificate && !isCertificateOptedOut && (
      ticket.status === "used" ||
      (eventHasPassed && ticket.status !== "cancelled" && ticket.status !== "refunded")
    );
    const showCertificateUnavailable = eligibleForCertificate && isCertificateOptedOut && (
      ticket.status === "used" ||
      (eventHasPassed && ticket.status !== "cancelled" && ticket.status !== "refunded")
    );

    return (
      <div
        key={ticket.id}
        className={cn(
          "group flex flex-col sm:flex-row items-stretch gap-4 p-4 rounded-2xl border bg-card/50 backdrop-blur-sm transition-all duration-300",
          isPast 
            ? "border-border/50 opacity-80" 
            : "border-border hover:border-primary/40 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:hover:shadow-[0_8px_30px_rgb(0,0,0,0.2)] hover:-translate-y-0.5"
        )}
      >
        {/* Event image/icon */}
        <div className={cn(
          "w-full sm:w-28 h-32 sm:h-28 rounded-xl overflow-hidden shrink-0 relative",
          isPast && "grayscale grayscale-[50%]"
        )}>
          <EventImage
            src={ticket.events?.cover_image_url}
            alt={ticket.events?.title || "Evento"}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          {isToday && activeTab === "active" && (
            <div className="absolute top-2 left-2 px-2 py-1 bg-primary text-primary-foreground text-[10px] font-bold uppercase rounded-md">
              {t("tickets.card.today")}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-0.5">
              <h3 className="font-display font-bold text-foreground text-lg leading-tight group-hover:text-primary transition-colors line-clamp-1">
                {ticket.events?.title}
              </h3>
              <p className="text-sm font-medium text-muted-foreground line-clamp-1">
                {ticket.ticket_tiers?.name}
              </p>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-1.5">
              {ticket.status !== "active" && <OrderStatusBadge status={ticket.status} />}
              {ticket.orders?.payment_method === "boleto" && ticket.orders?.boleto_url && (
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-md bg-warning/15 text-warning border border-warning/20">
                  {t("tickets.card.boletoPendingBadge")}
                </span>
              )}
              {isForResale && (
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-md bg-accent/20 text-accent border border-accent/20">
                  {t("tickets.card.resaleBadge")}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-x-4 gap-y-1.5 mt-2">
            {eventDate && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5 text-primary/70 shrink-0" />
                <span className="truncate">
                  {eventDate.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
                </span>
              </div>
            )}
            {eventDate && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5 text-primary/70 shrink-0" />
                <span>
                  {eventDate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            )}
            {ticket.events?.venue_city && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground col-span-2 sm:col-auto">
                <MapPin className="h-3.5 w-3.5 text-primary/70 shrink-0" />
                <span className="truncate">{ticket.events.venue_city}</span>
              </div>
            )}
          </div>

          {ticket.attendee_name && (
            <div className="mt-2 flex items-center gap-2 px-2 py-1 bg-secondary/50 rounded-lg w-fit max-w-full">
               <Mail className="h-3 w-3 text-muted-foreground shrink-0" />
               <p className="text-[10px] text-muted-foreground font-medium truncate uppercase tracking-wider">
                 {ticket.attendee_name}
               </p>
            </div>
          )}

          {ticket.orders?.payment_method === "boleto" && ticket.orders?.boleto_url && (
            <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-warning/5 border border-warning/20 text-warning text-xs">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-warning" />
              <div className="space-y-0.5">
                <p className="font-medium">{t("tickets.card.boletoUnpaidTitle")}</p>
                <p className="text-warning/80">
                  {t("tickets.card.boletoUnpaidDescPrefix")}<strong>30 dias</strong>{t("tickets.card.boletoUnpaidDescSuffix")}{" "}
                  <button
                    onClick={() => navigate(`/pedido/${ticket.order_id}`)}
                    className="underline font-medium hover:text-warning/100"
                  >
                    {t("tickets.card.viewBoleto")}
                  </button>
                </p>
              </div>
            </div>
          )}


        </div>

        {/* Actions - Bottom on mobile, Side on Desktop */}
        <div className="flex items-center gap-2 sm:flex-col sm:justify-center pt-3 sm:pt-0 sm:pl-4 border-t sm:border-t-0 sm:border-l border-border/50 shrink-0">
          {ticket.status === "active" && (
            <Button
              variant="default"
              size="sm"
              className="flex-1 sm:flex-none gap-2 h-9 px-4 rounded-lg shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              onClick={() => navigate(`/meus-ingressos/${ticket.id}`)}
            >
              <Eye className="h-4 w-4" /> 
              <span className="sm:hidden lg:inline">{t("tickets.card.access")}</span>
            </Button>
          )}

          {canEmitCertificate && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none gap-2 h-9 px-4 rounded-lg border-primary/30 text-primary hover:bg-primary/5 hover:border-primary/50 hover:-translate-y-0.5 transition-all duration-200"
              onClick={() => handleEmitCertificate(ticket)}
              disabled={isEmittingCertificateFor === ticket.id}
            >
              <Award className="h-4 w-4" />
              <span className="sm:hidden lg:inline">
                {isEmittingCertificateFor === ticket.id ? t("tickets.card.emitting") : t("tickets.card.certificate")}
              </span>
            </Button>
          )}

          {showCertificateUnavailable && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none gap-2 h-9 px-4 rounded-lg border-muted-foreground/20 text-muted-foreground"
              disabled
              title={t("tickets.card.certificateUnavailable")}
            >
              <Award className="h-4 w-4" />
              <span className="sm:hidden lg:inline">
                {t("tickets.card.certificateUnavailable")}
              </span>
            </Button>
          )}



          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9 shrink-0 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors" 
                aria-label={t("tickets.card.moreOptions")}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-xl shadow-xl">
              {ticket.status === "active" && (
                <DropdownMenuItem onClick={() => navigate(`/meus-ingressos/${ticket.id}`)}>
                  <Eye className="mr-2 h-4 w-4" /> {t("tickets.card.viewTicket")}
                </DropdownMenuItem>
              )}

              {ticket.status === "active" && ticket.events?.start_date && (
                <DropdownMenuItem
                  onClick={() => {
                    const ev = ticket.events;
                    if (!ev) return;
                    const location = [ev.venue_name, ev.venue_city].filter(Boolean).join(", ");
                    const url = generateGoogleCalendarUrl({
                      title: ev.title,
                      startDate: ev.start_date,
                      endDate: ev.start_date,
                      location,
                    });
                    window.open(url, "_blank");
                  }}
                >
                  <CalendarPlus className="mr-2 h-4 w-4" /> {t("tickets.card.addToCalendar")}
                </DropdownMenuItem>
              )}

              {ticket.status === "active" && (
                <DropdownMenuItem
                  onClick={async () => {
                    const logPrefix = "[MeusIngressos][send-ticket-email]";
                    console.log(`${logPrefix} === INÍCIO DO ENVIO ===`);
                    console.log(`${logPrefix} ticket.id:`, ticket.id);
                    console.log(`${logPrefix} ticket.attendee_email:`, ticket.attendee_email);
                    console.log(`${logPrefix} user?.email:`, user?.email);
                    console.log(`${logPrefix} session?.access_token exists:`, !!session?.access_token);
                    console.log(`${logPrefix} session?.access_token prefix:`, session?.access_token ? session.access_token.slice(0, 12) : "NULL");
                    console.log(`${logPrefix} session?.expires_at:`, session?.expires_at);

                    const recipientEmail = ticket.attendee_email || user?.email;
                    const qrCode = resolveTicketQrCode(ticket.qr_code, ticket.id);
                    const payload = {
                      ticketId: ticket.id,
                      recipientEmail,
                      eventTitle: ticket.events?.title,
                      tierName: ticket.ticket_tiers?.name,
                      qrCode,
                    };
                    console.log(`${logPrefix} payload:`, JSON.stringify(payload, null, 2));

                    try {
                      console.log(`${logPrefix} chamando invokeAuthenticatedFunction...`);
                      const result = await invokeAuthenticatedFunction("send-ticket-email", payload, session?.access_token ?? undefined);
                      console.log(`${logPrefix} SUCESSO! resultado:`, result);
                      toast({ title: t("tickets.toasts.emailSentTitle"), description: t("tickets.toasts.emailSentDesc") });
                    } catch (err: any) {
                      console.error(`${logPrefix} === ERRO NO ENVIO ===`);
                      console.error(`${logPrefix} erro.message:`, err?.message);
                      console.error(`${logPrefix} erro completo:`, err);
                      if (err?.context) {
                        console.error(`${logPrefix} erro.context (Response):`, err.context);
                      }
                      toast({ title: t("tickets.toasts.emailErrorTitle"), description: err?.message || t("tickets.toasts.unknownError"), variant: "destructive" });
                    }
                    console.log(`${logPrefix} === FIM DO ENVIO ===`);
                  }}
                >
                  <Mail className="mr-2 h-4 w-4" /> {t("tickets.card.resendEmail")}
                </DropdownMenuItem>
              )}

              {canEmitCertificate && (
                <DropdownMenuItem onClick={() => handleEmitCertificate(ticket)} disabled={isEmittingCertificateFor === ticket.id}>
                  <Award className="mr-2 h-4 w-4" />
                  {isEmittingCertificateFor === ticket.id ? t("tickets.card.emittingCertificate") : t("tickets.card.emitCertificate")}
                </DropdownMenuItem>
              )}

              {showCertificateUnavailable && (
                <DropdownMenuItem disabled>
                  <Award className="mr-2 h-4 w-4" />
                  {t("tickets.card.certificateUnavailable")}
                </DropdownMenuItem>
              )}

              {(ticket.status === "active" || isForResale) && <DropdownMenuSeparator />}

              {isTransferable && !isForResale && (
                <DropdownMenuItem
                  onClick={() =>
                    setTransferModal({
                      open: true,
                      ticketId: ticket.id,
                      eventTitle: ticket.events?.title || "",
                      tierName: ticket.ticket_tiers?.name || "",
                    })
                  }
                >
                  <Send className="mr-2 h-4 w-4" /> {t("tickets.card.transferTicket")}
                </DropdownMenuItem>
              )}

              {ticket.status === "active" && !isForResale && (
                <DropdownMenuItem
                  onClick={() => {
                    if (!isResellable) {
                      toast({
                        title: t("tickets.toasts.resaleUnavailableTitle"),
                        description: t("tickets.toasts.resaleUnavailableDesc"),
                        variant: "destructive",
                      });
                      return;
                    }
                    setResaleModal({ open: true, ticket });
                  }}
                >
                  <Repeat className="mr-2 h-4 w-4" /> {isResellable ? t("tickets.card.resellTicket") : t("tickets.card.resaleUnavailable")}
                </DropdownMenuItem>
              )}

              {isForResale && activeListing && (
                <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => handleCancelResale(activeListing.id, ticket.id)}>
                  <XCircle className="mr-2 h-4 w-4" /> {t("tickets.card.cancelResale")}
                </DropdownMenuItem>
              )}

              {(activeTab === "past" || activeTab === "archived") && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => toggleArchive(ticket.id)}>
                    <Archive className="mr-2 h-4 w-4" />
                    {archivedIds.includes(ticket.id) ? t("tickets.card.unarchive") : t("tickets.card.archive")}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  const getEmptyMessage = () => {
    switch (activeTab) {
      case "active":
        return t("tickets.empty.active");
      case "pending":
        return t("tickets.empty.pending");
      case "cancelled":
        return t("tickets.empty.cancelled");
      case "past":
        return t("tickets.empty.past");
      case "archived":
        return t("tickets.empty.archived");
    }
  };

  return (
    <>
      <SEOHead title={t("tickets.title")} description={t("tickets.subtitle")} />
      <div className="container pt-4 lg:pt-24 pb-16 pb-safe">
        {/* Header with title and search */}
        <div className="mb-8 space-y-1.5">
          <h1 className="font-display text-2xl lg:text-3xl font-bold text-foreground">{t("tickets.title")}</h1>
          <p className="text-muted-foreground">{t("tickets.subtitle")}</p>
          
          <div className="pt-4 w-full lg:w-80">
            <SearchInput
              type="text"
              placeholder={t("tickets.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pr-10 bg-muted/50 border-border focus:bg-background transition-all"
            />
          </div>
        </div>

        {/* Tabs - contained scroll */}
        <div className="flex border-b border-border mb-6 overflow-x-auto no-scrollbar scroll-smooth" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab" 
              aria-selected={activeTab === tab.id} 
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "pb-3 px-1 mr-6 text-sm font-semibold transition-all border-b-2 whitespace-nowrap relative",
                activeTab === tab.id
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
              )}
            >
              <span className="flex items-center gap-1.5">
                {tab.label}
                {tab.count > 0 && (
                  <span className={cn(
                    "text-[10px] px-1.5 py-0.5 rounded-full",
                    activeTab === tab.id ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                  )}>
                    {tab.count}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32 w-full rounded-2xl" />)}
          </div>
        ) : filteredTickets.length > 0 ? (
          <div className="grid grid-cols-1 gap-4">
            {filteredTickets.map(renderTicket)}
          </div>
        ) : (
          <EmptyState
            icon={<Ticket className="h-12 w-12" />}
            title={getEmptyMessage()}
            description={t("tickets.empty.description")}
            actionLabel={t("tickets.empty.action")}
            onAction={() => navigate("/eventos")}
          />
        )}
      </div>

      <TransferTicketModal
        open={transferModal.open}
        onOpenChange={(open) => setTransferModal((p) => ({ ...p, open }))}
        ticketId={transferModal.ticketId}
        eventTitle={transferModal.eventTitle}
        tierName={transferModal.tierName}
      />

      {resaleModal.ticket && (
        <ResaleListingModal
          open={resaleModal.open}
          onOpenChange={(open) => setResaleModal((p) => ({ ...p, open }))}
          ticket={resaleModal.ticket}
        />
      )}

      {downloadPayload && (
        <div
          ref={hiddenCertificateRef}
          className="fixed"
          aria-hidden="true"
          style={{ left: "-9999px", top: 0, width: "841.89pt", height: "595.28pt" }}
        >
          <CertificatePreview
            backgroundUrl={downloadPayload.config.backgroundUrl}
            fields={downloadPayload.config.fields}
            textConfig={downloadPayload.config.textConfig}
            signers={downloadPayload.config.signers}
            workloadHours={downloadPayload.config.workloadHours}
            sampleData={downloadPayload.sampleData}
            textColor={downloadPayload.config.textColor}
            textPositions={downloadPayload.config.textPositions}
            fontSizes={downloadPayload.config.fontSizes}
            elements={downloadPayload.config.elements}
            mode="producer"
          />
        </div>
      )}
    </>
  );
}
